import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiClient } from '../api/client';
import { API_ENDPOINTS } from '../api/endpoints';

interface ModifierOption {
  id: string;
  name: string;
  nameEn?: string;
  priceChange: number;
}

interface Modifier {
  id: string;
  name: string;
  nameEn?: string;
  type: 'single' | 'multiple' | 'free_text';
  options: ModifierOption[];
  isRequired: boolean;
}

interface ItemModifierLink {
  itemId: string;
  modifierId: string;
}

interface NumberFormat {
  decimalPlaces: number;
  thousandsSeparator: string;
  decimalSeparator: string;
}

interface DateFormat {
  type: 'gregorian' | 'hijri';
  format: string;
}

interface POSDisplaySettings {
  numberSize: 'small' | 'medium' | 'large' | 'xlarge';
  numberColor: string;
  totalSize: 'small' | 'medium' | 'large' | 'xlarge';
  totalColor: string;
  productSize: 'small' | 'medium' | 'large' | 'xlarge';
  iconColor: string;
  itemsPerRow: number;
}

interface PrinterSettings {
  cashierPrinterName: string;
  kitchenPrinterName: string;
  autoPrintCashier: boolean;
  autoPrintKitchen: boolean;
  printCashierOnSave: boolean;
  printKitchenOnSave: boolean;
}

interface SettingsState {
  numberFormat: NumberFormat;
  dateFormat: DateFormat;
  posDisplaySettings: POSDisplaySettings;
  printerSettings: PrinterSettings;
  modifiers: Modifier[];
  itemModifierLinks: ItemModifierLink[];
  isLoading: boolean;
  fetchSettings: () => Promise<void>;
  updateNumberFormat: (format: Partial<NumberFormat>) => Promise<void>;
  updateDateFormat: (format: Partial<DateFormat>) => Promise<void>;
  updatePOSDisplaySettings: (settings: Partial<POSDisplaySettings>) => Promise<void>;
  updatePrinterSettings: (settings: Partial<PrinterSettings>) => Promise<void>;
  addModifier: (modifier: Modifier) => void;
  updateModifier: (modifierId: string, modifier: Partial<Modifier>) => void;
  deleteModifier: (modifierId: string) => void;
  linkModifierToItem: (itemId: string, modifierId: string) => void;
  unlinkModifierFromItem: (itemId: string, modifierId: string) => void;
  getModifiersForItem: (itemId: string) => Modifier[];
}

const defaultNumberFormat: NumberFormat = {
  decimalPlaces: 2,
  thousandsSeparator: ',',
  decimalSeparator: '.',
};

const defaultDateFormat: DateFormat = {
  type: 'gregorian',
  format: 'dd/MM/yyyy',
};

const defaultPOSDisplaySettings: POSDisplaySettings = {
  numberSize: 'medium',
  numberColor: '#1f2937', // gray-800
  totalSize: 'large',
  totalColor: '#1f2937', // gray-800
  productSize: 'medium',
  iconColor: '#9ca3af', // gray-400
  itemsPerRow: 0, // 0 means auto
};

const defaultPrinterSettings: PrinterSettings = {
  cashierPrinterName: '',
  kitchenPrinterName: '',
  autoPrintCashier: true,
  autoPrintKitchen: true,
  printCashierOnSave: true,
  printKitchenOnSave: true,
};

const defaultModifiers: Modifier[] = [
  {
    id: 'mod_1',
    name: 'إضافات',
    nameEn: 'Add-ons',
    type: 'multiple',
    isRequired: false,
    options: [
      { id: 'opt_1_1', name: 'جبنة إضافية', nameEn: 'Extra Cheese', priceChange: 3 },
      { id: 'opt_1_2', name: 'كاتشب إضافي', nameEn: 'Extra Ketchup', priceChange: 0 },
      { id: 'opt_1_3', name: 'مايونيز', nameEn: 'Mayonnaise', priceChange: 0 },
    ],
  },
  {
    id: 'mod_2',
    name: 'بدون',
    nameEn: 'Without',
    type: 'multiple',
    isRequired: false,
    options: [
      { id: 'opt_2_1', name: 'بدون ثوم', nameEn: 'No Garlic', priceChange: 0 },
      { id: 'opt_2_2', name: 'بدون بندورة', nameEn: 'No Tomato', priceChange: 0 },
      { id: 'opt_2_3', name: 'بدون كاتشب', nameEn: 'No Ketchup', priceChange: 0 },
      { id: 'opt_2_4', name: 'بدون بصل', nameEn: 'No Onion', priceChange: 0 },
    ],
  },
  {
    id: 'mod_3',
    name: 'ملاحظات خاصة',
    nameEn: 'Special Notes',
    type: 'free_text',
    isRequired: false,
    options: [],
  },
];

const defaultItemModifierLinks: ItemModifierLink[] = [];

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => ({
      numberFormat: defaultNumberFormat,
      dateFormat: defaultDateFormat,
      posDisplaySettings: defaultPOSDisplaySettings,
      printerSettings: defaultPrinterSettings,
      modifiers: defaultModifiers,
      itemModifierLinks: defaultItemModifierLinks,
      isLoading: false,

      fetchSettings: async () => {
        set({ isLoading: true });
        try {
          const [numberFormatRes, dateFormatRes, posDisplayRes] = await Promise.all([
            apiClient.get(API_ENDPOINTS.systemSettings.numberFormat).catch(() => ({ data: defaultNumberFormat })),
            apiClient.get(API_ENDPOINTS.systemSettings.dateFormat).catch(() => ({ data: defaultDateFormat })),
            Promise.all([
              apiClient.get(`${API_ENDPOINTS.systemSettings.base}/POS.NumberSize`).catch(() => ({ data: { settingValue: defaultPOSDisplaySettings.numberSize } })),
              apiClient.get(`${API_ENDPOINTS.systemSettings.base}/POS.NumberColor`).catch(() => ({ data: { settingValue: defaultPOSDisplaySettings.numberColor } })),
              apiClient.get(`${API_ENDPOINTS.systemSettings.base}/POS.TotalSize`).catch(() => ({ data: { settingValue: defaultPOSDisplaySettings.totalSize } })),
              apiClient.get(`${API_ENDPOINTS.systemSettings.base}/POS.TotalColor`).catch(() => ({ data: { settingValue: defaultPOSDisplaySettings.totalColor } })),
              apiClient.get(`${API_ENDPOINTS.systemSettings.base}/POS.ProductSize`).catch(() => ({ data: { settingValue: defaultPOSDisplaySettings.productSize } })),
              apiClient.get(`${API_ENDPOINTS.systemSettings.base}/POS.IconColor`).catch(() => ({ data: { settingValue: defaultPOSDisplaySettings.iconColor } })),
              apiClient.get(`${API_ENDPOINTS.systemSettings.base}/POS.ItemsPerRow`).catch(() => ({ data: { settingValue: defaultPOSDisplaySettings.itemsPerRow } })),
            ]),
          ]);

          const posSettings = {
            numberSize: (posDisplayRes[0]?.data?.settingValue || defaultPOSDisplaySettings.numberSize) as POSDisplaySettings['numberSize'],
            numberColor: posDisplayRes[1]?.data?.settingValue || defaultPOSDisplaySettings.numberColor,
            totalSize: (posDisplayRes[2]?.data?.settingValue || defaultPOSDisplaySettings.totalSize) as POSDisplaySettings['totalSize'],
            totalColor: posDisplayRes[3]?.data?.settingValue || defaultPOSDisplaySettings.totalColor,
            productSize: (posDisplayRes[4]?.data?.settingValue || defaultPOSDisplaySettings.productSize) as POSDisplaySettings['productSize'],
            iconColor: posDisplayRes[5]?.data?.settingValue || defaultPOSDisplaySettings.iconColor,
            itemsPerRow: parseInt(posDisplayRes[6]?.data?.settingValue || defaultPOSDisplaySettings.itemsPerRow),
          };

          set({
            numberFormat: numberFormatRes.data || defaultNumberFormat,
            dateFormat: dateFormatRes.data || defaultDateFormat,
            posDisplaySettings: posSettings,
            isLoading: false,
          });
        } catch (error) {
          console.error('Error fetching settings:', error);
          set({ 
            numberFormat: defaultNumberFormat,
            dateFormat: defaultDateFormat,
            posDisplaySettings: defaultPOSDisplaySettings,
            isLoading: false 
          });
        }
      },

      updateNumberFormat: async (format: Partial<NumberFormat>) => {
        try {
          const current = get().numberFormat;
          const updated = { ...current, ...format };

          // Update individual settings
          await Promise.all([
            apiClient.post(API_ENDPOINTS.systemSettings.base, {
              settingKey: 'NumberFormat.DecimalPlaces',
              settingValue: updated.decimalPlaces.toString(),
              settingType: 'number',
            }),
            apiClient.post(API_ENDPOINTS.systemSettings.base, {
              settingKey: 'NumberFormat.ThousandsSeparator',
              settingValue: updated.thousandsSeparator,
              settingType: 'string',
            }),
            apiClient.post(API_ENDPOINTS.systemSettings.base, {
              settingKey: 'NumberFormat.DecimalSeparator',
              settingValue: updated.decimalSeparator,
              settingType: 'string',
            }),
          ]);

          set({ numberFormat: updated });
        } catch (error) {
          console.error('Error updating number format:', error);
          throw error;
        }
      },

      updateDateFormat: async (format: Partial<DateFormat>) => {
        try {
          const current = get().dateFormat;
          const updated = { ...current, ...format };

          // Update individual settings
          await Promise.all([
            apiClient.post(API_ENDPOINTS.systemSettings.base, {
              settingKey: 'DateFormat.Type',
              settingValue: updated.type,
              settingType: 'string',
            }),
            apiClient.post(API_ENDPOINTS.systemSettings.base, {
              settingKey: 'DateFormat.Format',
              settingValue: updated.format,
              settingType: 'string',
            }),
          ]);

          set({ dateFormat: updated });
        } catch (error) {
          console.error('Error updating date format:', error);
          throw error;
        }
      },

      updatePOSDisplaySettings: async (settings: Partial<POSDisplaySettings>) => {
        try {
          const current = get().posDisplaySettings;
          const updated = { ...current, ...settings };

          // Update individual settings
          const promises = [];
          if (settings.numberSize !== undefined) {
            promises.push(
              apiClient.post(API_ENDPOINTS.systemSettings.base, {
                settingKey: 'POS.NumberSize',
                settingValue: updated.numberSize,
                settingType: 'string',
                description: 'POS Number Size',
                descriptionEn: 'POS Number Size',
              })
            );
          }
          if (settings.numberColor !== undefined) {
            promises.push(
              apiClient.post(API_ENDPOINTS.systemSettings.base, {
                settingKey: 'POS.NumberColor',
                settingValue: updated.numberColor,
                settingType: 'string',
                description: 'POS Number Color',
                descriptionEn: 'POS Number Color',
              })
            );
          }
          if (settings.totalSize !== undefined) {
            promises.push(
              apiClient.post(API_ENDPOINTS.systemSettings.base, {
                settingKey: 'POS.TotalSize',
                settingValue: updated.totalSize,
                settingType: 'string',
                description: 'POS Total Size',
                descriptionEn: 'POS Total Size',
              })
            );
          }
          if (settings.totalColor !== undefined) {
            promises.push(
              apiClient.post(API_ENDPOINTS.systemSettings.base, {
                settingKey: 'POS.TotalColor',
                settingValue: updated.totalColor,
                settingType: 'string',
                description: 'POS Total Color',
                descriptionEn: 'POS Total Color',
              })
            );
          }
          if (settings.productSize !== undefined) {
            promises.push(
              apiClient.post(API_ENDPOINTS.systemSettings.base, {
                settingKey: 'POS.ProductSize',
                settingValue: updated.productSize,
                settingType: 'string',
                description: 'POS Product Size',
                descriptionEn: 'POS Product Size',
              })
            );
          }
          if (settings.iconColor !== undefined) {
            promises.push(
              apiClient.post(API_ENDPOINTS.systemSettings.base, {
                settingKey: 'POS.IconColor',
                settingValue: updated.iconColor,
                settingType: 'string',
                description: 'POS Icon Color',
                descriptionEn: 'POS Icon Color',
              })
            );
          }
          if (settings.itemsPerRow !== undefined) {
            promises.push(
              apiClient.post(API_ENDPOINTS.systemSettings.base, {
                settingKey: 'POS.ItemsPerRow',
                settingValue: updated.itemsPerRow.toString(),
                settingType: 'number',
                description: 'POS Items Per Row',
                descriptionEn: 'POS Items Per Row',
              })
            );
          }

          await Promise.all(promises);
          set({ posDisplaySettings: updated });
        } catch (error) {
          console.error('Error updating POS display settings:', error);
          throw error;
        }
      },

      updatePrinterSettings: async (settings: Partial<PrinterSettings>) => {
        try {
          const current = get().printerSettings;
          const updated = { ...current, ...settings };
          set({ printerSettings: updated });
        } catch (error) {
          console.error('Error updating printer settings:', error);
          throw error;
        }
      },

      addModifier: (modifier: Modifier) => {
        set((state) => ({
          modifiers: [...state.modifiers, modifier],
        }));
      },

      updateModifier: (modifierId: string, modifierUpdates: Partial<Modifier>) => {
        set((state) => ({
          modifiers: state.modifiers.map((m) =>
            m.id === modifierId ? { ...m, ...modifierUpdates } : m
          ),
        }));
      },

      deleteModifier: (modifierId: string) => {
        set((state) => ({
          modifiers: state.modifiers.filter((m) => m.id !== modifierId),
          itemModifierLinks: state.itemModifierLinks.filter(
            (link) => link.modifierId !== modifierId
          ),
        }));
      },

      linkModifierToItem: (itemId: string, modifierId: string) => {
        set((state) => {
          const alreadyExists = state.itemModifierLinks.some(
            (link) => link.itemId === itemId && link.modifierId === modifierId
          );
          if (alreadyExists) return state;
          return {
            itemModifierLinks: [
              ...state.itemModifierLinks,
              { itemId, modifierId },
            ],
          };
        });
      },

      unlinkModifierFromItem: (itemId: string, modifierId: string) => {
        set((state) => ({
          itemModifierLinks: state.itemModifierLinks.filter(
            (link) => !(link.itemId === itemId && link.modifierId === modifierId)
          ),
        }));
      },

      getModifiersForItem: (itemId: string): Modifier[] => {
        const state = get();
        const linkedModifierIds = state.itemModifierLinks
          .filter((link) => link.itemId === itemId)
          .map((link) => link.modifierId);
        
        // If no modifiers are linked to this item, return all modifiers (by default)
        if (linkedModifierIds.length === 0) {
          return state.modifiers;
        }
        
        return state.modifiers.filter((m) => linkedModifierIds.includes(m.id));
      },
    }),
    {
      name: 'settings-storage',
      partialize: (state) => ({
        numberFormat: state.numberFormat,
        dateFormat: state.dateFormat,
        posDisplaySettings: state.posDisplaySettings,
        printerSettings: state.printerSettings,
        modifiers: state.modifiers,
        itemModifierLinks: state.itemModifierLinks,
      }),
    }
  )
);













